import React, { PropsWithChildren, ReactNode } from 'react';
import { SharedIcon } from '../../SharedIcon/SharedIcon';
import { useCollapse } from '../../../hooks/useCollapse';
import classNames from 'classnames';

interface CollapsibleCardProps {
  sectionKey: string;
  headerLeft: ReactNode;
  headerRight?: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  onCollapse?: () => void;
  onExpand?: () => void;
  defaultCollapsed?: boolean;
  arrowPosition?: 'left' | 'right';
}

export const CollapsibleCard = ({
  sectionKey,
  headerLeft,
  headerRight,
  className,
  headerClassName,
  contentClassName,
  onCollapse,
  onExpand,
  defaultCollapsed,
  arrowPosition = 'right',
  children,
}: PropsWithChildren<CollapsibleCardProps>) => {
  const contentId = React.useId();
  const { onToggleCollapse, isExpanded, show, isTransitioning, contentRef } =
    useCollapse(sectionKey, defaultCollapsed);

  const arrow = (
    <span className={!show ? 'collapse-arrow' : 'collapse-arrow collapsed'}>
      <SharedIcon iconName="keyboard_arrow_right" size="sm" />
    </span>
  );

  const handleToggle = () => {
    onToggleCollapse();
    if (isExpanded) {
      onCollapse?.();
    } else {
      onExpand?.();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleToggle();
  };

  return (
    <div className={className}>
      <div
        className={classNames(
          'fs-6 d-flex justify-content-between align-items-center header',
          headerClassName,
          { collapsed: !show }
        )}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <div className="d-flex align-items-center gap-2">
          {arrowPosition === 'left' && arrow}
          {headerLeft}
        </div>
        <div className="d-flex align-items-center gap-2">
          {headerRight}
          {arrowPosition === 'right' && arrow}
        </div>
      </div>
      <div
        id={contentId}
        ref={contentRef}
        className={classNames(isTransitioning ? 'collapsing' : 'collapse', {
          show: show,
        })}
        aria-hidden={!isExpanded}
      >
        <div className={contentClassName}>{children}</div>
      </div>
    </div>
  );
};
